import { AfterViewChecked, ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  getContactInfo, getCurrentStep, getFilteredProblemMenuItemCount, getProblemDetails,
  getProblemPhotos, getProblemTypeMenu, getSelectedCoordinates, getSelectedProblemMenuItem, getNewProblemUniqueId, getProblemLocation, getProblemQuestions
} from 'src/app/store';
import { environment } from 'src/environments/environment';

import {
  ContactInfo, DnvLatLng, FilterProblemMenuItems, GetProblemMenuConfig, ImageFile,
  ProblemMenuItem, ProblemMenuSelection, QuestionAnswer, SetContactInfo, SetCurrentStep, SetProblemDetails,
  RemovePhoto, SetSelectedCoordinates, SetSelectedProblemMenuItemConfig, UpdateProblemMenuDropdownState, CreateProblemStub,
  SetSelectedLocationNameOrigin, ProgressionStep, SetSelectedLocationName, SubmitProblem, GetProblemQuestions,
  SetAnsweredProblemQuestions, ResetAnsweredProblemQuestions, ImageFilePayload, SubmitPhoto, RemoveErrorPhotos, SaveProblemTypeSearchLog,
  ResetState, DaysOfTheWeek, PhoneType, QuestionTypes, PhoneTypeValue
} from 'src/app/store/application';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-landing-page',
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class LandingPageComponent implements OnInit, AfterViewChecked {
  @ViewChild('restoreModal') restoreModal: TemplateRef<any>;

  photoUploadErrorMsg = "";

  currentProgressStep: number = 1;
  problemMenuItems: ProblemMenuItem[] = [];
  selectedProblemMenuItem: ProblemMenuSelection | null = null;
  problemDetails = "";
  problemPhotos: ImageFile[] = [];
  contactInfoValid: boolean = false;
  contactInfo: ContactInfo;
  selectedLatLng: DnvLatLng | null;
  problemLocation: string;
  showRedirectionMsg = false;
  showIsUrgentPrompt = false;
  showRelatedLinks = false;
  relatedLinks: string[] = [];
  addSecondPhone = false;
  addThirdPhone = false;
  submissionReviewable = false;
  submissionPageViewed = false;
  needToRetraverse = false;
  filteredProblemsItemCount: number = 0;
  newProblemUniqueId = '';
  canCreateCodeCase = false;
  contactInfoRequired = false;
  canBypassContactEntry = false;
  locationNameDescription = '';
  isErrorPresentOnPage = false;
  shouldTriggerReRender = false;

  // special cases for RAP-914
  nameEmailReqOnly = false;
  nameAddressEmailReqOnly = false;
  
  problemRelatedQuestions: QuestionAnswer[] = [];
  problemAnswersBypassable: boolean = true;
  
  commonSearches: string[] = [
    'tree',
    'garbage',
    'parking',
    'dog',
    'street'
  ];
  
  isLoadingFromRestore: boolean = false;
  restorableHTMLContent: string = "";

  private ngUnsubscribe: Subject<void> = new Subject();
  problemMenuItems$: Observable<ProblemMenuItem[]>;
  selectedProblemMenuItem$: Observable<ProblemMenuSelection | null>;
  currentProgressStep$: Observable<number>;
  problemDetails$: Observable<string>;
  problemPhotos$: Observable<ImageFile[]>;
  contactInfo$: Observable<ContactInfo>;
  selectedLatLng$: Observable<DnvLatLng | null>;
  problemLocation$: Observable<string>;
  filteredProblemsItemCount$: Observable<number>;
  newProblemUniqueId$: Observable<string>;
  problemRelatedQuestions$: Observable<QuestionAnswer[]>;

  previousWindowHeight: number = 0;

  modal: NgbModalRef | null = null;

  isDev: boolean = environment.isDev;

  constructor(private store: Store<{}>, private changeDetector: ChangeDetectorRef, private modalService: NgbModal) {
    this.problemMenuItems$ = this.store.select(getProblemTypeMenu)
    this.selectedProblemMenuItem$ = this.store.select(getSelectedProblemMenuItem);
    this.currentProgressStep$ = this.store.select(getCurrentStep);
    this.problemDetails$ = this.store.select(getProblemDetails);
    this.problemPhotos$ = this.store.select(getProblemPhotos);
    this.contactInfo$ = this.store.select(getContactInfo);
    this.selectedLatLng$ = this.store.select(getSelectedCoordinates);
    this.problemLocation$ = this.store.select(getProblemLocation);
    this.filteredProblemsItemCount$ = this.store.select(getFilteredProblemMenuItemCount);
    this.newProblemUniqueId$ = this.store.select(getNewProblemUniqueId);
    this.problemRelatedQuestions$ = this.store.select(getProblemQuestions);
  }

  ngOnInit(): void {
    this.store.dispatch(new GetProblemMenuConfig());

    this.problemMenuItems$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(menuItems => {
      this.problemMenuItems = menuItems;
    });

    this.selectedProblemMenuItem$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((menuItem) => {
      this.selectedProblemMenuItem = menuItem;
      this.canCreateCodeCase = menuItem!.CanCreateCodeCase;
      this.contactInfoRequired = (
        menuItem!.CanCreateCodeCase || 
        this.getContactRequiredAnimals(menuItem!) ||
        this.getContactRequiredGarbage(menuItem!) ||
        this.getContactRequiredStreets(menuItem!) ||
        this.getContactRequiredParking(menuItem!)
      );

      if (!!menuItem?.ProblemMenuConfig_RefId && !this.isLoadingFromRestore) {
        this.store.dispatch(new GetProblemQuestions(menuItem?.ProblemMenuConfig_RefId));
      }

      // if (!!selectedProblemMenuItem?.ProblemMenuConfig_RefId) {
      //   this.store.dispatch(new ConvertProblemQuestions(selectedProblemMenuItem.AdditionalQuestions));
      // }

      this.nameAddressEmailReqOnly = (
        (this.getContactRequiredStreets(menuItem!) && menuItem!.MenuTwo === 'Traffic safety concern') ||
        (this.getContactRequiredParking(menuItem!) && menuItem!.MenuTwo === 'Congested street parking')
      );

      this.nameEmailReqOnly = this.getContactRequiredStreets(menuItem!) && !this.nameAddressEmailReqOnly;
      
      if (menuItem?.ProblemMenuConfig_RefId) {
        sessionStorage.setItem('selectedProblemMenuItem', JSON.stringify(menuItem));
        sessionStorage.setItem('contactInfoRequired', JSON.stringify(this.contactInfoRequired));
      }
    });

    this.currentProgressStep$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(currentProgressStep => {
      let isInitialLoad = (
        this.currentProgressStep === ProgressionStep.ProblemSelection && 
        currentProgressStep === this.currentProgressStep
      );

      this.currentProgressStep = currentProgressStep;
      this.scrollToTop();

      if (isInitialLoad && this.getIsProgressRestorable()) {
        let interval = setInterval(() => {
          if (this.problemMenuItems.length) {
            this.openRestoreProgressModal();
            clearInterval(interval);
          }
        }, 25);
      } else {
        if (currentProgressStep !== ProgressionStep.ProblemSelectionError && 
          currentProgressStep !== ProgressionStep.SubmissionComplete &&
          currentProgressStep !== ProgressionStep.SystemError) {

          sessionStorage.setItem("currentProgressStep", currentProgressStep.toString());
        }
      }
    });
    
    this.problemDetails$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(problemDetails => {
      this.problemDetails = problemDetails;
      if (problemDetails) {
        sessionStorage.setItem("selectedProblemDetails", problemDetails);
      }
    });
    
    this.problemPhotos$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(problemPhotos => {
      let photosTemp: ImageFile[] = [];
      problemPhotos.forEach(photo => {
        photosTemp.push({
          ...photo,
        });
      });

      // this.problemPhotos = [...photosTemp];

      if (photosTemp) {
        if (photosTemp.length === this.problemPhotos.length) {
          // The update is a change to one of the photo image statuses
          photosTemp.forEach(photoTemp => {
            let problemPhotoIndex = this.problemPhotos.findIndex(photo => photo.hash === photoTemp.hash);
            if (problemPhotoIndex !== -1) {
              this.problemPhotos[problemPhotoIndex].imageStatus = photoTemp.imageStatus;
              this.problemPhotos[problemPhotoIndex].progress = photoTemp.progress;
            }
          });

        } else if (photosTemp.length > this.problemPhotos.length) {
          // The update is a new photo being added
          photosTemp.forEach(photoTemp => {
            let photoExists = this.problemPhotos.some(photo => photo.hash === photoTemp.hash);
            if (!photoExists) {
              this.problemPhotos.push(photoTemp);
            }
          });

        } else {
          // the update is a photo being removed
          this.problemPhotos = [...photosTemp];
        }
      }
    });

    this.selectedLatLng$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(selectedLatLng => {
      this.selectedLatLng = selectedLatLng;

      if (selectedLatLng?.lat && selectedLatLng.lng) {
        sessionStorage.setItem("selectedLatLng", JSON.stringify(selectedLatLng));
      }
    });
    
    this.problemLocation$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(problemLocation => {
      this.problemLocation = problemLocation;

      if (problemLocation) {
        sessionStorage.setItem('selectedProblemLocation', problemLocation);
      }
    });
    
    this.contactInfo$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(contactInfo => {
      if (contactInfo.phone.length > 1) this.addSecondPhone = true;
      if (contactInfo.phone.length > 2) this.addThirdPhone = true;
      this.contactInfo = contactInfo;
      let { address, email, firstName, lastName, phone } = contactInfo;
      
      if (address || email || firstName || lastName || phone.length) {
        sessionStorage.setItem('selectedContacts', JSON.stringify(contactInfo));
      }
    });
    
    this.filteredProblemsItemCount$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(
      filteredProblemsItemCount => this.filteredProblemsItemCount = filteredProblemsItemCount
    );

    this.newProblemUniqueId$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(newProblemUniqueId => this.newProblemUniqueId = newProblemUniqueId);
    this.problemRelatedQuestions$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(problemRelatedQuestions => {
      let questionsToLoad = [...problemRelatedQuestions];
      
      if (this.isLoadingFromRestore) {
        questionsToLoad = JSON.parse((sessionStorage.getItem("selectedProblemQuestions") || "[]"));
      }
      
      this.problemRelatedQuestions = questionsToLoad
      this.problemAnswersBypassable = !questionsToLoad.some(question => question.isRequired);

      if (this.selectedProblemMenuItem?.ProblemMenuConfig_RefId) {
        sessionStorage.setItem("selectedProblemQuestions", JSON.stringify(questionsToLoad));
      }
    });
  }

  ngAfterViewChecked() {
    let newHeight = document.querySelector("html")!.offsetHeight;
    if (this.previousWindowHeight !== newHeight) {
      this.previousWindowHeight = newHeight;
      setTimeout(() => (window as any).resizeIframeHeight(newHeight), 25);
    }
  }

  openModal(modal: TemplateRef<any>) {
    this.modal = this.modalService.open(modal);
  }

  closeModal() {
    if (this.modal) {
      this.modal.close();
    }
  }

  openRestoreProgressModal() {
    this.generateRestoreModalHTML();
    this.openModal(this.restoreModal);
  }

  closeRestoreModal(shouldRestoreProgress: boolean) {
    if (shouldRestoreProgress) {
      this.isLoadingFromRestore = true;
      this.handleProgressRestore();
    } else {
      this.resetSessionStorage();
    }

    this.isLoadingFromRestore = false;
    this.closeModal();
  }

  grabSessionStorageData() {
    let savedStep = parseInt(sessionStorage.getItem('currentProgressStep') || "1");
    let problemMenuItem = sessionStorage.getItem("selectedProblemMenuItem");
    let latlng = sessionStorage.getItem("selectedLatLng");
    let locationName = sessionStorage.getItem("selectedProblemLocation");
    let questions = sessionStorage.getItem("selectedProblemQuestions");
    let details = sessionStorage.getItem("selectedProblemDetails");
    let contacts = sessionStorage.getItem("selectedContacts");
    let contactInfoRequired = sessionStorage.getItem('contactInfoRequired');

    return {
      savedStep,
      problemMenuItem: problemMenuItem !== null ? JSON.parse(problemMenuItem) : null,
      latlng: latlng !== null ? JSON.parse(latlng) : null,
      locationName: locationName !== null ? locationName : "",
      questions: questions !== null ? JSON.parse(questions) : [],
      details: details !== null ? details : "",
      contacts: contacts !== null ? JSON.parse(contacts) : null,
      contactInfoRequired: contactInfoRequired === "true" ? true : false
    };
  }

  getIsProgressRestorable() {
    let { 
      problemMenuItem, latlng, locationName, 
      questions, details, contacts, contactInfoRequired 
    } = this.grabSessionStorageData();
    
    return (
      problemMenuItem !== null || latlng !== null || locationName !== "" && ( 
        (questions.length > 0 ? details !== "" : true) || 
        (contactInfoRequired ? contacts !== null : true)
      )
    );
  }

  handleProgressRestore() {
    let { problemMenuItem, latlng, locationName, questions, details, contacts, savedStep } = this.grabSessionStorageData();
    // let savedStep = ProgressionStep.ProblemSelection;

    if (problemMenuItem) {
      this.setSelectedProblemMenuItem(problemMenuItem);
      // savedStep = ProgressionStep.LocationSelection;
    }

    if (latlng) {
      this.setSelectedCoordinates(latlng);
      
      if (locationName) {
        this.setSelectedLocationNameOrigin(locationName);
        this.setSelectedLocationName(locationName);
      }
      
      // savedStep = ProgressionStep.DetailsInput;
    }

    if (questions.length && questions.some((question: any) => question.answer)) {
      this.setAnsweredProblemQuestions(questions);

      // if (!questions.some((question: any) => question.isRequired && !question.answer)) {
      //   savedStep = ProgressionStep.ContactDetailsInput;
      // }
    }

    if (details) {
      this.setProblemDetails(details);

      // if (!questions.length) {
      //   savedStep = ProgressionStep.ContactDetailsInput;
      // }
    }

    if (contacts) {
      this.setProblemContactInfo(contacts);

      // if (this.submissionReviewable) {
      //   savedStep = ProgressionStep.ReviewPage;
      // }
    }

    this.store.dispatch(new SetCurrentStep(savedStep));
    this.createProblemStub(savedStep);
  }

  generateRestoreModalHTML() {
    let { problemMenuItem, latlng, locationName, questions, details, contacts } = this.grabSessionStorageData();
    let html = "";

    if (problemMenuItem) {
      html += `<div class='modal-menuItem'><b>Problem Type: </b> ${problemMenuItem.MenuOne}`;
      if (problemMenuItem.MenuTwo) html += ` > ${problemMenuItem.MenuTwo}`;
      if (problemMenuItem.MenuThree) html += ` > ${problemMenuItem.MenuThree}`;
      html += "</div>";
    }

    if (locationName) {
      html += `<div class='modal-location'><b>Location: </b> ${locationName}</div>`;
    }

    if (questions.length && questions.some((question: any) => question.answer)) {
      html += "<div class='modal-questions'><b>Extra details: </b><br>";
      questions.forEach((question: any) => {
        html += `Question: ${question.question}<br>`;
        html += `Answer: ${question.answer}<br><br>`;
      });
      
      html += '</div>';
    }

    if (details) {
      html += `<div class='modal-details'><b>Problem Description: </b><br> ${details}</div>`;
    }

    if (contacts) {
      html += "<div class='modal-contacts'><b>Contact Details:</b><br>";
      if (contacts.address) html += `Address: ${contacts.address}<br>`;
      if (contacts.email) html += `Email: ${contacts.email}<br>`;
      if (contacts.firstName) html += `First name: ${contacts.firstName}<br>`;
      if (contacts.lastName) html += `Last name: ${contacts.lastName}<br>`;
      if (contacts.notificationsRequested) html += `Notifications: ${contacts.notificationsRequested ? "Yes" : "No"}<br>`;
      
      contacts.phone.forEach((phone: PhoneType) => {
        let phoneType = phone.phoneType === "1" ? "Home" :
                        phone.phoneType === "2" ? "Cell" :
                        phone.phoneType === "3" ? "Work" :
                        "Other";

        html += `${phoneType}: ${phone.phoneNumber}<br>`;  
      });

      html += "</div>";
    }

    this.restorableHTMLContent = html;
  }

  resetSessionStorage() {
    sessionStorage.clear();
    sessionStorage.setItem("currentProgressStep", this.currentProgressStep.toString());
  }
  
  getContactRequiredAnimals(menuItem: ProblemMenuSelection): boolean {
    return menuItem.MenuOne === 'Animals' && (
      menuItem?.MenuTwo === 'Lost a pet' || menuItem?.MenuTwo === 'Found a pet'
    );
  }

  getContactRequiredGarbage(menuItem: ProblemMenuSelection): boolean {
    return menuItem?.MenuOne === 'Garbage, litter, pollution' && (
      menuItem.MenuTwo === 'Collection day issues' || menuItem.MenuThree === 'Garbage carts set out before 5:30am' ||
      menuItem.MenuTwo === 'Litter or illegal dumping' || menuItem.MenuTwo === 'Commercial garbage collection issue' ||
      menuItem.MenuTwo === 'Public street garbage can issue' || menuItem.MenuTwo === 'Residential garbage or organics not collected'
    );
  }

  getContactRequiredStreets(menuItem: ProblemMenuSelection): boolean {
    return menuItem?.MenuOne === 'Streets and sidewalks' && (
      menuItem.MenuTwo === 'Road marking missing' || menuItem.MenuTwo === 'Bus stop problem' || menuItem.MenuTwo === 'Road visibility blocked' ||
      menuItem.MenuTwo === 'Traffic safety concern' || (menuItem.MenuTwo === 'Street light, sign, or signal broken or missing' && (
        menuItem.MenuThree === 'Broken traffic signal' || menuItem.MenuThree === 'Missing or broken street sign'
      ))
    );
  }

  getContactRequiredParking(menuItem: ProblemMenuSelection): boolean {
    return menuItem?.MenuOne === 'Parking and vehicles' && (
      menuItem.MenuTwo === 'Congested street parking'
    )
  }

  updateProblemMenuDropDownState(updatedProblemMenu: ProblemMenuItem[]) {
    this.store.dispatch(new UpdateProblemMenuDropdownState(updatedProblemMenu));
  }

  filterProblemMenuItems(filterString: string) {
    this.store.dispatch(new FilterProblemMenuItems(filterString));
  }

  logSearchFieldText(filterString: string) {
    this.store.dispatch(new SaveProblemTypeSearchLog(filterString));
  }

  setProbSelectButtonTabIndex() {
    // need to start at one as the filter bar search field has an index of 1
    let index = 1;
    this.problemMenuItems.forEach(menu1 => {
      index++;

      if (menu1.shouldExpandDropdown) {
        menu1.subMenus.forEach(menu2 => {
          index++;

          if (menu2.shouldExpandDropdown) {
            menu2.subMenus.forEach(() => index++);
          }
        });
      }
    });

    if (this.submissionPageViewed) {
      if (!document.getElementById('problemSelectReviewSub')) {
        window.requestAnimationFrame(this.setProbSelectButtonTabIndex.bind(this));
      } else {
        (document.getElementById('problemSelectReviewSub') as any).tabIndex = index;
      }
    }

    // if (this.submissionPageViewed) {
    //   (document.getElementById('problemSelectReviewSub') as any).tabIndex = index;
    //   (document.getElementById('problemSelectNextPage') as any).tabIndex = index + 1;
    // } else {
    //   (document.getElementById('problemSelectNextPage') as any).tabIndex = index;
    // }
  }

  setSelectedProblemMenuItem(selectedProblemMenuItem: ProblemMenuSelection) {
    this.clearSubmissionDetails();
    this.store.dispatch(new SetSelectedProblemMenuItemConfig(selectedProblemMenuItem));
    // document.getElementById("navBtn")?.scrollIntoView(true);
  }

  setSelectedCoordinates(coordinates: DnvLatLng) {
    this.store.dispatch(new SetSelectedCoordinates(coordinates));
    this.setSubmissionReviewable();
    this.isErrorPresentOnPage = false;
  }

  setSelectedLocationNameOrigin(locationName: string) {
    this.store.dispatch(new SetSelectedLocationName(locationName));
    this.store.dispatch(new SetSelectedLocationNameOrigin(locationName));
    this.locationNameDescription = locationName;
  }

  setSelectedLocationName(locationName: string) {
    this.store.dispatch(new SetSelectedLocationName(locationName));
    this.locationNameDescription = locationName;
  }

  setProblemDetails(problemDetails: string) {
    this.store.dispatch(new SetProblemDetails(problemDetails));
    this.setSubmissionReviewable();

    if (!this.problemRelatedQuestions.length && problemDetails.length > 0) {
      this.isErrorPresentOnPage = false;
    }
  }

  addPhoto(photo: ImageFilePayload) {
    this.store.dispatch(new SubmitPhoto(photo));
  }

  removePhoto(hash: string) {
    this.store.dispatch(new RemovePhoto(hash));
  }

  checkPhoneNumbers(contactInfo: ContactInfo): boolean {
    let firstPhoneAdded: boolean = false;
    let secondPhoneAdded: boolean = false;
    let thirdPhoneAdded: boolean = false;
    let phone = null;

    if (contactInfo.phone.length === 0) {
      return false;
    }

    phone = contactInfo.phone[0];
    firstPhoneAdded = !!phone && !!phone.phoneType && phone.phoneType.length > 0 && phone.phoneNumber.length === 10;

    // we are only adding one extra phone number
    if (this.addSecondPhone) {
      if (contactInfo.phone.length < 2) {
        return false;
      }

      phone = contactInfo.phone[1];
      secondPhoneAdded = phone === undefined ? false : !!phone.phoneType && phone.phoneType.length > 0 && phone.phoneNumber.length === 10;

      if (!this.addThirdPhone) {
        return firstPhoneAdded && secondPhoneAdded;
      }
    }

    // we are added two extra phone numbers
    if (this.addThirdPhone) {
      if (contactInfo.phone.length < 3) {
        return false;
      }

      phone = contactInfo.phone[2];
      thirdPhoneAdded = phone === undefined ? false : !!phone.phoneType && phone.phoneType.length > 0 && phone.phoneNumber.length === 10;
      return firstPhoneAdded && secondPhoneAdded && thirdPhoneAdded;
    }

    // we are added no extra phone numbers
    return firstPhoneAdded;
  }

  setContactFormValidState(formStateValid: boolean) {
    this.contactInfoValid = formStateValid;
    if (formStateValid) {
      this.isErrorPresentOnPage = false;
    }

    this.changeDetector.detectChanges();
  }

  setProblemContactInfo(contactInfo: ContactInfo) {
    this.store.dispatch(new SetContactInfo(contactInfo));
    this.setSubmissionReviewable();
  }

  addExtraPhoneNumber() {
    if (!this.addSecondPhone) this.addSecondPhone = true;
    else this.addThirdPhone = true;
  }

  removeExtraPhoneNumber(indexToRemove: number) {
    let newContactInfo = { ...this.contactInfo };
    let newPhoneNumbers: PhoneType[] = [];

    // copy the main contact over first
    if (this.contactInfo.phone[0]) {
      newPhoneNumbers.push(this.contactInfo.phone[0]);
    }

    // are we deleting phoneNumber #2
    if (indexToRemove === 2) {

      // if a third phone number exists, shift it to the second spot
      if (this.addThirdPhone) {
        this.addThirdPhone = false;

        // only add if a number exists
        if (this.contactInfo.phone[2]) {
          newPhoneNumbers.push(this.contactInfo.phone[2]);
        }

      } else {
        this.addSecondPhone = false;
      }

    } else {

      // just add the second phone number
      this.addThirdPhone = false;

      // only add if a number exists
      if (this.contactInfo.phone[1]) {
        newPhoneNumbers.push(this.contactInfo.phone[1]);
      }
    }

    // if after all checks, there is only the first and it is blank, remove it from the list as the "optional" number will cause it to think its an error
    if (newPhoneNumbers.length === 1 && newPhoneNumbers[0].phoneNumber.length === 0) {
      newPhoneNumbers = [];
    }

    newContactInfo.phone = newPhoneNumbers;
    this.setProblemContactInfo(newContactInfo);
  }

  setSubmissionReviewable() {
    this.submissionReviewable = false;
    this.submissionReviewable = (
      !!this.selectedProblemMenuItem && !!this.selectedLatLng &&
      (this.problemRelatedQuestions.length > 0 || (this.problemRelatedQuestions.length === 0 && this.problemDetails.length > 0)) &&
      this.problemAnswersBypassable && this.contactInfoValid && !this.needToRetraverse
    );

    this.setShowRedirectionMsg();
  }

  setAnsweredProblemQuestions(questionAnswers: QuestionAnswer[]) {
    this.store.dispatch(new SetAnsweredProblemQuestions(questionAnswers));
    this.problemAnswersBypassable = !questionAnswers.some(question =>
      question.isRequired && question.questionViewable && 
      (
        (question.questionType === QuestionTypes.RadioButton && question.radioOptions?.includes("Other") && question.answer === "Other" && question.radioOtherOptionEntry?.length === 0) ||
        (question.checkOptionAnswers && !question.checkOptionAnswers.some(checkAnswer => checkAnswer.answer)) ||
        (question.questionType === QuestionTypes.ConfirmationToggle && !question.confirmationAnswer) ||
        (question.questionType !== QuestionTypes.Checkbox && question.questionType !== QuestionTypes.ConfirmationToggle && (question.answer as string).length === 0)
      )
    );

    if (this.problemAnswersBypassable) {
      this.isErrorPresentOnPage = false;
    }
  }

  scrollToTop() {
    document.body.scrollTop = 0; // For Safari
    document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    window.scrollTo(0, 0);
    (window as any).scrollToWindowTop()
  }

  triggerIframeExpansion() {
    setTimeout(() => (window as any).expandIframeContentToTop(), 75);
  }

  triggerIframeContraction() {
    setTimeout(() => (window as any).contractIframeContentToNormal(), 75);
  }

  isAfterHoursExceptionOverride(menuId: number | undefined) {
    let isExceptionRequired = false;
    if (menuId !== undefined) {
      switch (menuId) {
        case 16:
        case 75:
        case 76:
        case 77:
        case 78:
        case 83:
        case 91:
          let date = new Date();
          let currentDay = date.getDay();
          let currentHour = date.getHours();
          // let currentMinutes = date.getMinutes();

          // check if the current day a weekend
          if (currentDay === DaysOfTheWeek.Saturday || currentDay === DaysOfTheWeek.Sunday) {
            isExceptionRequired = true;
            break;
          }

          // check if the current time is outside of operating hours (8am - 4pm)
          if (currentHour < 8 || currentHour >= 16) {
            isExceptionRequired = true;
            break;
          }

          // DEV: SET EXCEPTION TO TRUE FOR TESTING
          // if (true) {
          //   isExceptionRequired = true;
          //   break;
          // }

          // no exception and is within working hours
          isExceptionRequired = false;
          break;

        default:
          isExceptionRequired = false;
          break;
      }
    }

    return isExceptionRequired;
  }

  isAfterHoursRedirectionException(problemMenuConfigId?: number) {
    // used in conjunction with the other after hours calculation function to get the redirection required specific problems only
    switch (problemMenuConfigId) {
      case 16:
      case 83:
        return false;

      default:
        return true;
    }
  }

  isSpecifiedRedirectionRequired(problemMenuConfigId?: number) {
    switch (problemMenuConfigId) {
      case 68:
      case 79:
        return true;

      default:
        return false;
    }
  }

  setShowRedirectionMsg() {
    this.showRedirectionMsg = false;
    this.showRedirectionMsg = (
      (this.selectedProblemMenuItem!.ProblemType_RefId === null && this.selectedProblemMenuItem!.Group_RefId === null) ||
      this.isSpecifiedRedirectionRequired(this.selectedProblemMenuItem?.ProblemMenuConfig_RefId) ||
      (
        // set them to match that dont equal so that they show URGENT (yellow) instead of HALT (red) error prompts
        this.isAfterHoursExceptionOverride(this.selectedProblemMenuItem?.ProblemMenuConfig_RefId) &&
        this.isAfterHoursRedirectionException(this.selectedProblemMenuItem?.ProblemMenuConfig_RefId)
      )
    );

    if (this.currentProgressStep === ProgressionStep.ProblemSelection) {
      this.setProbSelectButtonTabIndex();
    }
  }

  setShowRelatedLinks(menuId?: number) {
    switch (menuId) {
      case 86:
        this.showRelatedLinks = true;
        this.relatedLinks = 
          [
            `<a href="https://www.dnv.org/your-home-property/garbage-and-organics-we-collect" target="_blank">What can I recycle?</a>`,
            `<a href="https://www.dnv.org/your-home-property/where-drop-your-recycling" target="_blank">Where can I drop off my recycling?</a>`,
            `<a href="https://www.dnv.org/your-home-property/get-your-collection-schedule" target="_blank">Look up my collection day</a>`
          ];
        break;
    }
  }

  resetTriggerReRender() {
    this.shouldTriggerReRender = false;
    this.changeDetector.detectChanges();
  }

  goToNextStep() {
    this.isErrorPresentOnPage = false;

    if (this.currentProgressStep === ProgressionStep.ProblemSelection) {
      this.showIsUrgentPrompt = false;
      this.showRedirectionMsg = false;
      
      this.setShowRelatedLinks(this.selectedProblemMenuItem?.ProblemMenuConfig_RefId);
      
      this.setShowRedirectionMsg();
      if (this.showRedirectionMsg) {
        this.store.dispatch(new SetCurrentStep(ProgressionStep.ProblemSelectionError));
        return;

      } else {

        // handle cases where continuing is allowed
        switch (this.selectedProblemMenuItem?.ProblemMenuConfig_RefId) {
          case 54:
            this.store.dispatch(new SetCurrentStep(ProgressionStep.ProblemSelectionInfo));
            return;
          // case 7:
          case 50:
          case 51:
          case 52:

          // tree issues that need to show warning
          case 116:
          case 117:
          case 66:
          case 72:
          case 93:
          case 73:

            this.showIsUrgentPrompt = true;
            this.store.dispatch(new SetCurrentStep(ProgressionStep.ProblemSelectionError));
            return;

          case 16:
          case 83:
            if (this.isAfterHoursExceptionOverride(this.selectedProblemMenuItem.ProblemMenuConfig_RefId)) {
              this.showIsUrgentPrompt = true;
              this.store.dispatch(new SetCurrentStep(ProgressionStep.ProblemSelectionError));
              return;
            } else {
              break;
            }

          default:
            this.showIsUrgentPrompt = false;
        }
      }
    }
    
    if (this.currentProgressStep === ProgressionStep.LocationSelection) {
      this.isErrorPresentOnPage = this.selectedLatLng === null || (this.selectedLatLng.lat === 0 && this.selectedLatLng.lng === 0);

    } else if (this.currentProgressStep === ProgressionStep.DetailsInput) {
      this.isErrorPresentOnPage = (!this.problemRelatedQuestions.length && !this.problemDetails) || !this.problemAnswersBypassable;
      // this.isErrorPresentOnPage = (
      //   ((!this.problemRelatedQuestions.length || (this.problemRelatedQuestions.length && !this.problemRelatedQuestions.some(question => question.isRequired))) && !this.problemDetails) || 
      //   !this.problemAnswersBypassable
      // );
      
      // need this flag here as if this varible value isn't updated, it won't trigger an update automatically in the child component
      if (this.isErrorPresentOnPage) {
        this.shouldTriggerReRender = true;
      }
      
    } else if (this.currentProgressStep === ProgressionStep.ContactDetailsInput) {
      this.isErrorPresentOnPage = !this.contactInfoValid;
    }

    if (!this.isErrorPresentOnPage) {
      let nextStep = this.currentProgressStep !== ProgressionStep.ProblemSelectionError ?
        this.currentProgressStep + 1 : ProgressionStep.LocationSelection;

      this.store.dispatch(new SetCurrentStep(nextStep));
  
      if (nextStep === ProgressionStep.ContactDetailsInput) {
        // remove the block on the review submission button when you get to the your details page
        this.needToRetraverse = false;
        // this.canBypassContactEntry = !this.canCreateCodeCase && !this.checkPhoneNumbers(this.contactInfo);
        this.canBypassContactEntry = !this.contactInfoRequired && !this.checkPhoneNumbers(this.contactInfo);
  
        // remove photos that failed to upload from the store
        this.store.dispatch(new RemoveErrorPhotos())
      }
  
      if (nextStep === ProgressionStep.ReviewPage) {
        this.submissionReviewable = true;
        this.submissionPageViewed = true;
      }
  
      this.createProblemStub(nextStep);
    }
  }

  createProblemStub(nextStep: number) {
    if (this.newProblemUniqueId === '' && nextStep >= ProgressionStep.DetailsInput) {
      this.store.dispatch(new CreateProblemStub());
    }
  }

  goToPreviousStep() {
    this.store.dispatch(new SetCurrentStep(this.currentProgressStep - 1));
    this.isErrorPresentOnPage = false;
  }

  goToStep(step: number) {
    this.store.dispatch(new SetCurrentStep(step));

    if (step === ProgressionStep.ProblemSelection) {
      this.store.dispatch(new GetProblemMenuConfig());
    }

    this.isErrorPresentOnPage = false;
  }

  clearSubmissionDetails() {
    this.showRedirectionMsg = false;
    this.showIsUrgentPrompt = false;

    this.submissionReviewable = false;
    this.submissionPageViewed = false;
    this.needToRetraverse = true;

    let newContactInfo = Object.assign({}, this.contactInfo);
    // newContactInfo.address = this.canCreateCodeCase ? newContactInfo.address : "";
    newContactInfo.address = this.contactInfoRequired ? newContactInfo.address : "";

    this.store.dispatch(new SetContactInfo(newContactInfo));
    this.store.dispatch(new ResetAnsweredProblemQuestions());
  }

  purgeProgress() {
    this.showRedirectionMsg = false;
    this.showIsUrgentPrompt = false;

    this.submissionReviewable = false;
    this.submissionPageViewed = false;
    this.needToRetraverse = true;

    this.addSecondPhone = false;
    this.addThirdPhone = false;

    this.store.dispatch(new ResetState());
    this.goToStep(1);
  }

  submitNewProblem() {
    this.store.dispatch(new SubmitProblem());
    // this.store.dispatch(new SetCurrentStep(ProgressionStep.SubmissionComplete));
  }

  devProgressionStep(event: any) {
    this.goToStep(parseInt(event.target.value));
  }
}
